import FormFieldInfoTooltip from '@atoms/FormFieldInfoTooltip/FormFieldInfoTooltip';
import classNames from 'classnames';
import { ErrorMessage, Field, FieldProps, useField } from 'formik';
import {
  DetailedHTMLProps,
  ForwardedRef,
  InputHTMLAttributes,
  forwardRef,
} from 'react';

interface Props
  extends DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  readonly name: string;
  readonly label?: string;
  readonly optionalFieldLabel?: string;
  readonly maxCharsLabel?: string;
  readonly infoTooltip?: string;
  readonly component?: string | React.ComponentType<FieldProps>;
  readonly inputWrapperClass?: string;
  readonly inputClass?: string;
  readonly inputFieldPrefix?: JSX.Element;
  readonly inputFieldSuffix?: JSX.Element;
  readonly dataTestId?: string;
}

const FormInput = forwardRef(
  (
    {
      name,
      label,
      required,
      optionalFieldLabel,
      maxCharsLabel,
      infoTooltip,
      inputWrapperClass,
      inputClass,
      inputFieldPrefix,
      inputFieldSuffix,
      dataTestId,
      ...rest
    }: Props,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const [, meta] = useField(name);

    const { maxLength } = rest;

    return (
      <div className="flex flex-col space-y-1" data-testid={dataTestId}>
        {(label || infoTooltip) && (
          <div className="flex justify-between">
            {label && (
              <label
                data-testid="form-input-label"
                htmlFor={name}
                className={classNames('body-four', {
                  'text-red': meta.error && meta.touched,
                })}
              >
                {`${label}${required ? ' *' : ''}`}
                {optionalFieldLabel && !required ? (
                  <>
                    {' '}
                    <span className="text-grey-600">{optionalFieldLabel}</span>
                  </>
                ) : (
                  ''
                )}
                {maxCharsLabel && !!maxLength ? (
                  <>
                    {' '}
                    <span className="text-grey-600">{maxCharsLabel}</span>
                  </>
                ) : (
                  ''
                )}
              </label>
            )}

            {infoTooltip && <FormFieldInfoTooltip infoTooltip={infoTooltip} />}
          </div>
        )}

        <div className={inputWrapperClass}>
          {inputFieldPrefix}

          <Field
            {...rest}
            name={name}
            id={name}
            className={classNames(
              'body-three w-full appearance-none rounded-md bg-grey-100 px-5 py-4 text-black-100',
              {
                'ring-1 ring-red ring-inset': meta.error && meta.touched,
              },
              inputClass
            )}
            innerRef={ref}
          />

          {inputFieldSuffix}
        </div>

        {meta.error && meta.touched && (
          <ErrorMessage name={name}>
            {(msg: string) => (
              <span
                data-testid="form-input-error-message"
                className="body-four text-red"
              >
                {msg}
              </span>
            )}
          </ErrorMessage>
        )}
      </div>
    );
  }
);

export default FormInput;
